import { BlurView } from 'expo-blur';
import { TextClassContext } from './text';
import { GLASS } from '@sixam/config/glass';
import { MOTION } from '@sixam/config/motion';
import { cn } from '../utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

const pressEasing = Easing.bezier(...MOTION.easingBezier.outExpo);

const buttonVariants = cva(
  cn(
    'group relative shrink-0 flex-row items-center justify-center overflow-hidden rounded-xl border shadow-none',
    Platform.select({
      web: "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive whitespace-nowrap outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
    })
  ),
  {
    variants: {
      variant: {
        default: cn(
          'border-primary bg-primary active:bg-primary/90',
          Platform.select({ web: 'hover:bg-primary/90' })
        ),
        destructive: cn(
          'border-destructive bg-destructive active:bg-destructive/90',
          Platform.select({
            web: 'hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
          })
        ),
        outline: cn(
          'border-black/35 bg-black/25 active:bg-black/40 dark:border-black/35 dark:active:bg-black/40',
          Platform.select({
            web: 'hover:bg-black/40',
          })
        ),
        secondary: cn(
          'border-secondary bg-secondary active:bg-secondary/80',
          Platform.select({ web: 'hover:bg-secondary/80' })
        ),
        ghost: cn(
          'border-transparent active:bg-accent dark:active:bg-accent',
          Platform.select({ web: 'hover:bg-accent dark:hover:bg-accent/50' })
        ),
        link: 'border-transparent',
      },
      size: {
        default: cn('h-12 px-5 py-2 sm:h-11', Platform.select({ web: 'has-[>svg]:px-4' })),
        sm: cn('h-10 gap-1.5 px-3 sm:h-9', Platform.select({ web: 'has-[>svg]:px-2.5' })),
        lg: cn('h-14 px-7 sm:h-12', Platform.select({ web: 'has-[>svg]:px-5' })),
        icon: 'h-11 w-11 sm:h-10 sm:w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const buttonTextVariants = cva(
  cn(
    'font-sans text-sm font-bold uppercase tracking-widest text-foreground',
    Platform.select({ web: 'pointer-events-none transition-colors' })
  ),
  {
    variants: {
      variant: {
        default: 'text-primary-foreground',
        destructive: 'text-white',
        outline: 'text-white',
        secondary: 'text-secondary-foreground',
        ghost: 'group-active:text-accent-foreground',
        link: cn(
          'text-primary group-active:underline',
          Platform.select({ web: 'underline-offset-4 hover:underline group-hover:underline' })
        ),
      },
      size: {
        default: '',
        sm: '',
        lg: '',
        icon: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

type ButtonProps = React.ComponentProps<typeof Pressable> &
  React.RefAttributes<typeof Pressable> &
  VariantProps<typeof buttonVariants>;

function releaseWebPointerFocus(event: unknown) {
  const pointerEvent = event as {
    nativeEvent?: {
      pointerType?: string;
    };
  };

  if (Platform.OS !== 'web' || !pointerEvent.nativeEvent?.pointerType) {
    return;
  }

  const activeElement = globalThis.document?.activeElement;

  if (activeElement instanceof HTMLElement) {
    activeElement.blur();
  }
}

function Button({ className, variant, size, ...props }: ButtonProps) {
  const { children, disabled, onPress, onPressIn, onPressOut, ...pressableProps } = props;
  const reducedMotion = useReducedMotion();
  const scale = useSharedValue(1);
  const isGlassControl = variant === 'outline';

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const handlePressIn = React.useCallback<NonNullable<ButtonProps['onPressIn']>>(
    (event) => {
      if (!disabled && !reducedMotion) {
        scale.set(
          withTiming(MOTION.press.scale, {
            duration: MOTION.duration.feedback,
            easing: pressEasing,
          })
        );
      }
      releaseWebPointerFocus(event);
      onPressIn?.(event);
    },
    [disabled, onPressIn, reducedMotion, scale]
  );

  const handlePressOut = React.useCallback<NonNullable<ButtonProps['onPressOut']>>(
    (event) => {
      if (!disabled && !reducedMotion) {
        scale.set(
          withTiming(1, {
            duration: MOTION.duration.feedback,
            easing: pressEasing,
          })
        );
      }
      onPressOut?.(event);
    },
    [disabled, onPressOut, reducedMotion, scale]
  );

  const handlePress = React.useCallback<NonNullable<ButtonProps['onPress']>>(
    (event) => {
      releaseWebPointerFocus(event);
      onPress?.(event);
    },
    [onPress]
  );

  return (
    <Animated.View style={animatedStyle}>
      <TextClassContext.Provider
        value={{ className: buttonTextVariants({ variant, size }), style: { letterSpacing: 2.4 } }}
      >
        <Pressable
          className={cn(disabled && 'opacity-50', buttonVariants({ variant, size }), className)}
          disabled={disabled}
          onPress={handlePress}
          onPressIn={handlePressIn}
          onPressOut={handlePressOut}
          role="button"
          {...pressableProps}
        >
          {(state) => (
            <>
              {isGlassControl ? (
                <>
                  <BlurView
                    intensity={GLASS.blur.surface}
                    tint="dark"
                    style={StyleSheet.absoluteFill}
                  />
                  <View
                    style={[
                      StyleSheet.absoluteFill,
                      {
                        backgroundColor: GLASS.overlay.control,
                        pointerEvents: 'none',
                      },
                    ]}
                  />
                </>
              ) : null}
              <View className="relative flex-row items-center justify-center gap-2">
                {typeof children === 'function' ? children(state) : children}
              </View>
            </>
          )}
        </Pressable>
      </TextClassContext.Provider>
    </Animated.View>
  );
}

export { Button, buttonTextVariants, buttonVariants };
export type { ButtonProps };
